/**
 * M95 — declarative Level plans for long Stage 2 matches.
 * Plans describe targets; wave tables are materialized separately (see escalate + waveBuilders).
 */

export type LevelArcPhaseId =
  | 'intro'
  | 'develop'
  | 'pressure'
  | 'checkpoint'
  | 'climax'
  | 'boss'

export interface LevelArcPhaseSpec {
  id: LevelArcPhaseId
  /** 1-based inclusive first wave index in this level. */
  waveStart: number
  /** 1-based inclusive last wave index in this level. */
  waveEnd: number
  /** Optional author note (tactics, tower hints). */
  note?: string
}

export interface LevelPlan {
  /** Level ordinal within the Stage (e.g. 8 → display 2-8). */
  ordinal: number
  mapId: string
  /** Authored target wave count for this level (see targetWaveCountForOrdinal). */
  waveCountTarget: number
  /** Campaign V3 boss-level flag (typically every 5th level). */
  bossLevel: boolean
  /** Tower allow-list; omit to mean all Chapter 2 towers at integration time. */
  allowedTowerIds?: readonly string[]
  /** Segments the match arc for pacing / escalation helpers. */
  arcPhases: LevelArcPhaseSpec[]
}

const CH2_ALL_TOWERS = ['fire', 'frost', 'cannon', 'poison', 'aa', 'mage'] as const

/** Product policy: every 5th Level is a Boss Level unless explicitly overridden. */
export function bossLevelForOrdinal(ordinal: number): boolean {
  return ordinal > 0 && ordinal % 5 === 0
}

/**
 * Deterministic wave-count targets from the Stage 2 long-level brief:
 * - 2-1…2-4: 10–14 waves
 * - 2-5…2-29: interpolate toward late-act counts
 * - 2-30: 35–40 waves (returns 38 — mid-band default for authoring)
 */
export function targetWaveCountForOrdinal(ordinal: number): number {
  const n = Math.max(1, Math.min(30, Math.floor(ordinal)))
  if (n <= 4) {
    // 2-1→10, 2-2→11, 2-3→12, 2-4→14 (within 10–14 band)
    return n === 4 ? 14 : 9 + n
  }
  if (n >= 30) return 38
  const t = (n - 5) / (30 - 5)
  return Math.round(14 + t * (38 - 14))
}

/** Resolve which arc phase a 1-based wave index falls under. */
export function arcPhaseForWave(
  plan: LevelPlan,
  waveIndex: number
): LevelArcPhaseSpec | undefined {
  return plan.arcPhases.find(
    (p) => waveIndex >= p.waveStart && waveIndex <= p.waveEnd
  )
}

/**
 * Default arc segmentation for long levels — deterministic from wave count + boss flag.
 * Boss levels reserve the final ~8% of waves for climax + boss (min 2 waves).
 */
export function defaultArcPhases(
  waveCount: number,
  bossLevel: boolean
): LevelArcPhaseSpec[] {
  const w = Math.max(1, waveCount)

  if (bossLevel && w <= 4) {
    return [
      { id: 'intro', waveStart: 1, waveEnd: Math.max(1, w - 1) },
      { id: 'boss', waveStart: w, waveEnd: w, note: 'Boss wave' }
    ]
  }

  const bossTail = bossLevel ? Math.min(2, Math.max(1, Math.floor(w * 0.12))) : 0
  const bodyEnd = w - bossTail

  const introEnd = Math.min(4, Math.max(1, Math.floor(w * 0.1)))
  const developEnd = Math.min(bodyEnd, Math.max(introEnd + 1, Math.floor(w * 0.35)))
  const pressureEnd = Math.min(
    bodyEnd,
    Math.max(developEnd + 1, Math.floor(w * 0.65))
  )
  const checkpointEnd = Math.min(
    bodyEnd,
    Math.max(pressureEnd, Math.floor(w * 0.82))
  )

  const phases: LevelArcPhaseSpec[] = [
    { id: 'intro', waveStart: 1, waveEnd: introEnd },
    { id: 'develop', waveStart: introEnd + 1, waveEnd: developEnd },
    { id: 'pressure', waveStart: developEnd + 1, waveEnd: pressureEnd },
    { id: 'checkpoint', waveStart: pressureEnd + 1, waveEnd: checkpointEnd }
  ]

  if (bossLevel) {
    const climaxStart = checkpointEnd + 1
    const bossStart = Math.max(climaxStart, w - bossTail + 1)
    if (climaxStart < bossStart) {
      phases.push({
        id: 'climax',
        waveStart: climaxStart,
        waveEnd: bossStart - 1
      })
    }
    phases.push({
      id: 'boss',
      waveStart: bossStart,
      waveEnd: w,
      note: 'Boss wave(s) + support'
    })
  } else if (checkpointEnd < w) {
    phases.push({ id: 'climax', waveStart: checkpointEnd + 1, waveEnd: w })
  }

  return mergeAdjacentArcPhases(phases)
}

function mergeAdjacentArcPhases(phases: LevelArcPhaseSpec[]): LevelArcPhaseSpec[] {
  const out: LevelArcPhaseSpec[] = []
  for (const p of phases) {
    if (p.waveStart > p.waveEnd) continue
    const prev = out[out.length - 1]
    if (prev && prev.id === p.id && prev.waveEnd + 1 === p.waveStart) {
      prev.waveEnd = p.waveEnd
      continue
    }
    out.push({ ...p })
  }
  return out
}

/** Build a LevelPlan scaffold for Chapter 2 long-level migration (M95+). */
export function chapter2LevelPlan(
  ordinal: number,
  mapId: string,
  partial?: Partial<
    Pick<LevelPlan, 'waveCountTarget' | 'bossLevel' | 'allowedTowerIds' | 'arcPhases'>
  >
): LevelPlan {
  const waveCountTarget = partial?.waveCountTarget ?? targetWaveCountForOrdinal(ordinal)
  const bossLevel = partial?.bossLevel ?? bossLevelForOrdinal(ordinal)
  return {
    ordinal,
    mapId,
    waveCountTarget,
    bossLevel,
    allowedTowerIds: partial?.allowedTowerIds ?? [...CH2_ALL_TOWERS],
    arcPhases:
      partial?.arcPhases ?? defaultArcPhases(waveCountTarget, bossLevel)
  }
}

export { CH2_ALL_TOWERS }
